import { Button, Stack, Typography } from '@mui/material';
import { FunctionComponent, Ref, useRef, useState } from 'react';
import { Command, useGameCommands } from '../../store/GameCommands';
import { useAnimationsDisabled } from '../../util/Animation';
import { settingsStore, useSettingsSelector } from '../../store/SettingsStore';
import { waitForSeconds } from '../../util/Async';
import { useKeyPressHandler } from '../../util/Keyboard';
import { muiStyleSheet } from '../../util/Styles';
import { compareCaseInsensitive } from '../../util/Text';
import { ConfirmDialog } from '../ConfirmDialog';
import { GamePanel } from './GamePanel';
import { KeyLabel } from './KeyLabel';

export const ControlPanel: FunctionComponent = () => {
	const { commands, answerCount, title } = useGameCommands();
	const buttonRefsMap = useRef<Map<string, HTMLButtonElement>>(new Map());
	const [confirmingCommand, setConfirmingCommand] = useState<Command | undefined>(undefined);
	const animationsDisabled = useAnimationsDisabled();

	async function triggerRippleEffect(commandId: string) {
		if (!animationsDisabled) {
			const button = buttonRefsMap.current.get(commandId);
			const eventOptions = { bubbles: true, cancelable: true, view: window, };
			button?.dispatchEvent(new MouseEvent('mousedown', eventOptions));
			await waitForSeconds(0.1);
			button?.dispatchEvent(new MouseEvent('mouseup', eventOptions));
		}
	}

	function runCommand(command: Command) {
		if (command.confirmMessage) {
			setConfirmingCommand(command);
		} else {
			command.action();
		}
	}

	// Handle key presses at the panel level so they work even when collapsed
	useKeyPressHandler((key: string) => {
		const command = commands.find(c => compareCaseInsensitive(key, c.key));
		if (command) {
			runCommand(command);
			void triggerRippleEffect(command.id);
		}
		return command !== undefined;
	}, [commands]);

	// TODO: Adjust title based on game phase?
	return <>
		<GamePanel title={title}>
			{commands.empty()
				? <Typography variant="body2" color="text.secondary">
					No commands available
				</Typography>
				: <Stack direction='column' spacing={1}>
					{answerCount && <AnswersButton answerCount={answerCount} />}
					{commands.map((command) => <CommandButton
						key={command.id}
						command={command}
						onRefChange={(ref) => {
							if (ref) {
								buttonRefsMap.current.set(command.id, ref);
							} else {
								buttonRefsMap.current.delete(command.id);
							}
						}}
						onClick={() => runCommand(command)}
					/>)}
				</Stack>
			}
		</GamePanel>
		{confirmingCommand && <ConfirmDialog
			open
			title={confirmingCommand.label}
			message={confirmingCommand.confirmMessage ?? ''}
			onClose={() => setConfirmingCommand(undefined)}
			onConfirm={confirmingCommand.action}
		/>}
	</>;
};

type CommandButtonProps = {
	command: Command;
	onRefChange: Ref<HTMLButtonElement>;
	onClick: () => void;
};

const CommandButton: FunctionComponent<CommandButtonProps> = (props) => {
	const { command, onRefChange, onClick } = props;
	const controlKeysEnabled = useSettingsSelector(settingsStore.selectors.gameControlKeysEnabled);

	return <Button
		ref={onRefChange}
		variant="outlined"
		onClick={onClick}
		aria-label={`${command.label}. ${controlKeysEnabled ? `Shortcut key ${command.key}` : ''}`}
		sx={[styles.button, command.ruleOverride ? styles.ruleOverride : {}]}
	>
		{command.label}
		{controlKeysEnabled && <KeyLabel>{command.key}</KeyLabel>}
	</Button>;
};

type AnswersButtonProps = { answerCount: number; };

/** 
 * Special case of CommandButton that indicates answers and their keyboard shortcuts are available.
 * The button itself is not clickable and doesn't handle the keypresses. @see <AnswerCard>.
 */
const AnswersButton: FunctionComponent<AnswersButtonProps> = (props) => {
	return <Button variant="outlined" disableRipple
		aria-label={`Answers. Shortcut keys one to ${props.answerCount}`}
		sx={styles.button}>
		Answer
		<KeyLabel>1</KeyLabel>
		-
		<KeyLabel sx={{ marginLeft: 0 }}>{props.answerCount}</KeyLabel>
	</Button>;
};

const styles = muiStyleSheet({
	button: {
		justifyContent: 'space-between',
		textTransform: 'none',
		fontWeight: 'normal',
	},
	ruleOverride: {
		opacity: 0.5,
	},
});